import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ModalConfirmationComponent } from 'src/app/components/modal-confirmation/modal-confirmation.component';
import { MenuService } from 'src/app/components/menu/service/menu.service';
import { Config } from 'src/app/services/auth/Config';
import {
  canRetryOAuth,
  clearOAuthPkceData,
  getCodeVerifier,
  OAUTH_CODE_STORAGE_KEY,
  registerOAuthAttemptFailure,
  resetOAuthAttemptCount,
} from 'src/app/services/auth/pkce-storage.util';
import { TokenValidationService } from 'src/app/services/auth/token-validation/token-validation.service';
import { LoggedInService } from 'src/app/services/auth/loggedin.service';
import { Token } from 'src/app/shared/interfaces/token-data';
import { AppLoaderService } from 'src/app/shared/services/app-loader.service';
import { AppModalService } from 'src/app/shared/services/app-modal.service';
import { AppConfigService } from 'src/environments/app-config.service';

@Injectable()
export class AuthService {
  private readonly config: Config;
  private readonly environment: any;

  constructor(
    private readonly loggedInService: LoggedInService,
    private readonly http: HttpClient,
    private readonly loaderService: AppLoaderService,
    private readonly router: Router,
    private readonly menuService: MenuService,
    private readonly appConfig: AppConfigService,
    private readonly tokenValidationService: TokenValidationService,
    private readonly modalService: AppModalService,
  ) {
    this.environment = this.appConfig.config;
    this.config = new Config();
  }

  public getAccessToken(code: string): void {
    const codeVerifier = getCodeVerifier();

    if (!codeVerifier) {
      this.handleOAuthFailure();
      return;
    }

    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded'),
    };
    const body = new URLSearchParams();

    body.set('grant_type', this.environment.grant_type);
    body.set('code', code);
    body.set('redirect_uri', this.environment.redirect_url);
    body.set('client_id', this.environment.client_id);
    body.set('code_verifier', codeVerifier);

    this.http.post<Token>(`${this.environment.url_base}/as/token.oauth2`, body.toString(), options)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (this.isInvalidGrantError(error)) {
            this.handleOAuthFailure();
          }

          return throwError(() => error);
        }),
        finalize(() => this.loaderService.hide()),
      )
      .subscribe({
        next: token => {
          this.storeAccessToken(token);
          clearOAuthPkceData();
          resetOAuthAttemptCount();
          this.menuService.getOptionsMenu(
            this.environment.apiIssuer,
            this.environment.acxPlataformRedirectUrl,
            true,
          );

          this.stripOAuthCodeFromUrl();
        },
        error: () => undefined,
      });
  }

  public isAuthenticated(code: string): boolean {
    this.loaderService.showLoader('dark');
    sessionStorage.setItem(OAUTH_CODE_STORAGE_KEY, code);
    document.cookie = 'session=true';
    this.getAccessToken(code);

    return true;
  }

  public ensureMenuLoaded(): boolean {
    this.menuService.getOptionsMenu(this.environment.apiIssuer, this.environment.acxPlataformRedirectUrl);

    return true;
  }

  /** @deprecated Use ensureMenuLoaded(). */
  public isReloadingMenu(): boolean {
    return this.ensureMenuLoaded();
  }

  public localstorageContainsToken(): boolean {
    const token = sessionStorage.getItem('PING_ACCESS_TOKEN');

    if (token) {
      return this.loggedInService.handleTokenExpiration(token);
    }

    return true;
  }

  public logout(): void {
    clearOAuthPkceData();
    resetOAuthAttemptCount();
    sessionStorage.clear();
    window.location.href = `${this.environment.url_base}/idp/startSLO.ping`;
  }

  private storeAccessToken(token: Token): void {
    sessionStorage.setItem('PING_ACCESS_TOKEN', token.access_token);
    sessionStorage.setItem('PING_REFRESH_TOKEN', token.refresh_token);
    sessionStorage.setItem('PING_TOKEN_TYPE', token.token_type);
    sessionStorage.setItem('PING_EXPIRES_IN', token.expires_in.toString());
    sessionStorage.setItem('PING_ID_TOKEN', token.id_token);
    this.loggedInService.syncTokenContentFromSession();
  }

  private handleOAuthFailure(): void {
    registerOAuthAttemptFailure();

    if (canRetryOAuth()) {
      this.restartOAuthLogin();
      return;
    }

    this.showOAuthExhaustedError();
  }

  private restartOAuthLogin(): void {
    clearOAuthPkceData();
    this.tokenValidationService.pingRedirect();
  }

  private showOAuthExhaustedError(): void {
    this.loaderService.hide();
    clearOAuthPkceData();
    this.stripOAuthCodeFromUrl();

    this.modalService.open(ModalConfirmationComponent, {
      modalTitleType: 'error',
      modalType: 'error',
      title: 'Não foi possível concluir o acesso',
      content: 'Não foi possível validar seu acesso após duas tentativas. Tente novamente ou entre em contato com o suporte.',
      closeButton: true,
      tryAgainButton: true,
      supportB3Button: true,
    }).afterClosed$.subscribe(() => {
      resetOAuthAttemptCount();
      this.tokenValidationService.pingRedirect();
    });
  }

  private stripOAuthCodeFromUrl(): void {
    const urlTree = this.router.parseUrl(this.router.url);
    delete urlTree.queryParams['code'];
    this.router.navigateByUrl(urlTree, { replaceUrl: true });
  }

  private isInvalidGrantError(error: HttpErrorResponse): boolean {
    const oauthError = error.error?.error ?? error.error?.['error'];

    return error.status === 400 && oauthError === 'invalid_grant';
  }
}
